import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VideoCamera, ArrowClockwise, Trash, Info, Copy, Eye, MagnifyingGlass, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const METHOD_COLORS = {
  GET: "bg-blue-100 text-blue-800 border-blue-300",
  POST: "bg-green-100 text-green-800 border-green-300",
  PUT: "bg-amber-100 text-amber-800 border-amber-300",
  PATCH: "bg-orange-100 text-orange-800 border-orange-300",
  DELETE: "bg-red-100 text-red-800 border-red-300",
  HEAD: "bg-gray-100 text-gray-700 border-gray-300",
  OPTIONS: "bg-purple-100 text-purple-800 border-purple-300",
};

function formatTimestamp(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

function bytesPretty(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function tryFormatJson(text) {
  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch { return text; }
}

async function copyToClipboard(text, label = "Kopyalandı") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Kopyalanamadı");
  }
}

export default function CameraRawLogs() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [limit, setLimit] = useState("100");
  const [methodFilter, setMethodFilter] = useState("all");
  const [plateFilter, setPlateFilter] = useState("");
  const [plateQuery, setPlateQuery] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setPlateQuery(plateFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [plateFilter]);

  const { data, loading, refetch } = useLiveQuery(
    [TAGS.CAMERAS],
    async () => {
      const params = new URLSearchParams();
      params.set("limit", limit);
      if (methodFilter !== "all") params.set("method", methodFilter);
      if (plateQuery) params.set("plate", plateQuery);
      const r = await api.get(`/camera-raw-logs?${params.toString()}`);
      return r.data;
    },
    { deps: [limit, methodFilter, plateQuery], intervalMs: 15000 },
  );

  const items = data?.items || [];
  const total = data?.total ?? 0;

  const deleteOne = async (id) => {
    if (!isSuperAdmin) return;
    if (!confirm("Bu ham kayıt silinsin mi?")) return;
    try {
      await api.delete(`/camera-raw-logs/${id}`);
      toast.success("Kayıt silindi");
      refetch?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const deleteAll = async () => {
    if (!isSuperAdmin) return;
    if (!confirm(`${total} adet ham kamera kaydının tümünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      const r = await api.delete("/camera-raw-logs");
      toast.success(`${r.data?.deleted ?? 0} kayıt silindi`);
      refetch?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Entegrasyon Ayıklama</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <VideoCamera size={28} className="text-primary" />
            Kamera Ham Veri
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Dahua ITSAPI kameralarının <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/NotificationInfo/TollgateInfo</code> adresine
            gönderdiği tüm ham HTTP istekleri.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refetch?.()}
            className="rounded-sm uppercase tracking-widest text-xs"
            data-testid="cameraraw-refresh"
          >
            <ArrowClockwise size={14} /> Yenile
          </Button>
          {isSuperAdmin && total > 0 && (
            <Button
              variant="destructive"
              onClick={deleteAll}
              className="rounded-sm uppercase tracking-widest text-xs"
              data-testid="cameraraw-delete-all"
            >
              <Trash size={14} /> Tümünü Sil
            </Button>
          )}
        </div>
      </div>

      {/* Integration hint */}
      <Card className="rounded-sm border-blue-200 bg-blue-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={20} className="text-blue-700 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 space-y-2 flex-1">
            <div className="font-semibold uppercase tracking-widest">Kamera Konfigürasyonu</div>
            <div className="space-y-1">
              <div>Dahua ITSAPI kameraları için &quot;HTTP Push&quot; hedef URL&apos;si:</div>
              <div className="font-mono bg-white border border-blue-300 rounded px-2 py-1 flex items-center justify-between">
                <span>{window.location.origin}/api/NotificationInfo/TollgateInfo</span>
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/api/NotificationInfo/TollgateInfo`, "URL kopyalandı")}
                  className="text-blue-700 hover:text-blue-900"
                  data-testid="cameraraw-copy-url"
                >
                  <Copy size={12} />
                </button>
              </div>
              <div className="text-blue-800/80">
                Bazı Dahua modelleri yolu sabit tutar. Reverse-proxy&apos;niz <code>/NotificationInfo/*</code> isteklerini
                backend&apos;e yönlendiriyorsa <code>{window.location.origin.replace(/^https?:/, "http:")}/NotificationInfo/TollgateInfo</code>
                {" "}(HTTP) ve HTTPS varyantı da çalışır. Her iki yol da aynı log tablosuna yazar.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Plaka Ara</div>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={plateFilter}
                  onChange={(e) => setPlateFilter(e.target.value.toUpperCase())}
                  placeholder="34ABC123"
                  className="rounded-sm h-10 pl-8 font-mono uppercase"
                  data-testid="cameraraw-plate-filter"
                />
                {plateFilter && (
                  <button type="button" onClick={() => setPlateFilter("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="cameraraw-plate-filter-clear" aria-label="Temizle">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">HTTP Method</div>
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="rounded-sm h-10" data-testid="cameraraw-method-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Kayıt Limiti</div>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger className="rounded-sm h-10" data-testid="cameraraw-limit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">Son 25</SelectItem>
                  <SelectItem value="100">Son 100</SelectItem>
                  <SelectItem value="250">Son 250</SelectItem>
                  <SelectItem value="500">Son 500</SelectItem>
                  <SelectItem value="1000">Son 1000</SelectItem>
                  <SelectItem value="2000">Son 2000</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="text-xs text-muted-foreground">
                Toplam: <span className="font-mono font-bold text-foreground" data-testid="cameraraw-total">{total}</span> ham kayıt
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Yükleniyor...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <VideoCamera size={40} className="text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">Henüz ham kamera kaydı yok.</div>
              <div className="text-xs text-muted-foreground/70 mt-1">
                Kameranız yukarıdaki URL&apos;ye istek gönderdiğinde otomatik olarak burada listelenecek.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Zaman</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Method</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Yol</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">İstemci IP</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Content-Type</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Body</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">İşlenen</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`cameraraw-row-${it.id}`}>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatTimestamp(it.timestamp)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold font-mono uppercase px-2 py-0.5 rounded border ${METHOD_COLORS[it.method] || "bg-gray-100 text-gray-700 border-gray-300"}`}>
                          {it.method}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs max-w-[280px] truncate" title={it.url}>{it.path}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{it.client_ip || "—"}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={it.content_type || ""}>
                        {it.content_type || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        <span title={it.body_truncated ? "Kesildi (>500KB)" : ""}>{bytesPretty(it.content_length)}</span>
                        {it.body_truncated && <span className="text-amber-600 ml-1">⚠</span>}
                      </td>
                      <td className="px-3 py-2 text-xs" data-testid={`cameraraw-processed-${it.id}`}>
                        {it.processed?.ok ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-green-300 bg-green-50 text-green-800 font-mono text-[10px]"
                            title={`Plaka: ${it.processed.plate} · Kamera: ${it.processed.camera_id || "-"} · Yön: ${it.processed.direction || "-"}`}
                          >
                            {it.processed.plate}{" "}
                            <span className="opacity-70">·</span>{" "}
                            {it.processed.direction === "giris" ? "GİRİŞ" : it.processed.direction === "cikis" ? "ÇIKIŞ" : it.processed.direction || "?"}
                          </span>
                        ) : it.processed && it.processed.ok === false ? (
                          (() => {
                            const reason = it.processed.reason;
                            const REASON_MAP = {
                              duplicate: { label: "MÜKERRER", cls: "border-slate-300 bg-slate-50 text-slate-700" },
                              already_inside: { label: "İÇERİDE", cls: "border-cyan-300 bg-cyan-50 text-cyan-800" },
                              blacklisted: { label: "KARA LİSTE", cls: "border-red-400 bg-red-100 text-red-900" },
                              lot_full: { label: "OTOPARK DOLU", cls: "border-red-400 bg-red-100 text-red-900" },
                              plate_flicker: { label: "TİTREME", cls: "border-amber-300 bg-amber-50 text-amber-800" },
                              phantom_entry_prevented: { label: "FANTOM ENGELLENDI", cls: "border-blue-300 bg-blue-50 text-blue-800" },
                              duplicate_after_exit: { label: "COOLDOWN", cls: "border-purple-300 bg-purple-50 text-purple-800" },
                              dedupe_locked: { label: "DEDUPE", cls: "border-gray-300 bg-gray-50 text-gray-800" },
                              no_open_session: { label: "GİRİŞ YOK", cls: "border-orange-300 bg-orange-50 text-orange-800" },
                              low_confidence: { label: "DÜŞÜK GÜVEN", cls: "border-yellow-300 bg-yellow-50 text-yellow-800" },
                              unrecognized_format_needs_review: { label: "TANINMAYAN FORMAT", cls: "border-rose-300 bg-rose-50 text-rose-800" },
                            };
                            const meta = reason && REASON_MAP[reason];
                            if (meta) {
                              const matched = it.processed.matched_plate;
                              return (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-mono text-[10px] ${meta.cls}`}
                                  title={`Plaka: ${it.processed.plate}${matched ? ` · Benzer: ${matched}` : ""}${it.processed.distance != null ? ` · Mesafe: ${it.processed.distance}` : ""} · Sebep: ${reason}`}
                                >
                                  {it.processed.plate || "—"}
                                  <span className="opacity-70">·</span>
                                  {meta.label}
                                  {matched && (<><span className="opacity-70">·</span>{matched}</>)}
                                </span>
                              );
                            }
                            return (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-red-300 bg-red-50 text-red-800 font-mono text-[10px]"
                                title={it.processed.error || reason || "İşleme hatası"}
                              >
                                {reason ? reason.toUpperCase() : "HATA"}
                              </span>
                            );
                          })()
                        ) : it.skip_reason ? (
                          (() => {
                            // Sessizce kaybolmuş event — plaka yok / JSON değil
                            // / vb. Panelde gri "⏭️ İŞLENMEDİ" olarak göster.
                            const SKIP_LABELS = {
                              no_plate_detected: "PLAKA YOK",
                              camera_reported_no_plate: "KAMERA: PLAKA YOK",
                              not_json_payload: "JSON DEĞİL",
                              invalid_json: "BOZUK JSON",
                              payload_not_dict_or_list: "GEÇERSİZ ŞEKİL",
                            };
                            const label = SKIP_LABELS[it.skip_reason] || it.skip_reason.toUpperCase();
                            return (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-neutral-300 bg-neutral-50 text-neutral-600 font-mono text-[10px]"
                                title={`İşlenmedi — sebep: ${it.skip_reason}`}
                                data-testid={`cameraraw-skip-${it.id}`}
                              >
                                <span>⏭️</span>{label}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-muted-foreground text-[10px]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDetail(it)}
                          className="rounded-sm h-7 px-2"
                          data-testid={`cameraraw-view-${it.id}`}
                        >
                          <Eye size={12} />
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteOne(it.id)}
                            className="rounded-sm h-7 px-2 ml-1 text-destructive hover:bg-destructive/10"
                            data-testid={`cameraraw-delete-${it.id}`}
                          >
                            <Trash size={12} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className={`text-[10px] font-bold font-mono uppercase px-2 py-0.5 rounded border ${METHOD_COLORS[detail?.method] || ""}`}>
                {detail?.method}
              </span>
              <span className="font-mono text-sm">{detail?.path}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              {/* Sessizce kaybolmuş event kutusu — processed & process_error boş
                  ama skip_reason var. Panelde hiçbir olay görünmeden kaybolmasın. */}
              {!detail.processed && !detail.process_error && detail.skip_reason && (() => {
                const SKIP_META = {
                  no_plate_detected: {
                    title: "⏭️ İŞLENMEDİ — Plaka Tespit Edilemedi",
                    explain: "Ne JSON gövdesinde ne de JPEG rescue-path'inde bir plaka bulunabildi. Muhtemelen kamera olay tetikledi ama tam kadraj yakalayamadı, ya da OCR karakter döndürmedi. Session ÜRETİLMEDİ — bilgi amaçlı log.",
                  },
                  camera_reported_no_plate: {
                    title: "⏭️ İŞLENMEDİ — Kamera Açıkça 'Plaka Yok' Dedi",
                    explain: "Dahua bildiriminde Plate.IsExist=false veya PlateNumber boş geldi. Ön plakası olmayan motorbisiklet, dolu palet vb. için normal davranıştır. Rescue-path fantom plaka üretmesin diye guard devrede kaldı.",
                  },
                  not_json_payload: {
                    title: "⏭️ İŞLENMEDİ — JSON İçerik Değil",
                    explain: "Content-Type JSON değil ya da gövde boş. Genellikle sağlık kontrolü (health check), heartbeat veya reverse-proxy probe'u. Session ÜRETİLMEDİ.",
                  },
                  invalid_json: {
                    title: "⏭️ İŞLENMEDİ — Bozuk JSON",
                    explain: "Gövde JSON olarak parse edilemedi. Kamera firmware'i eksik yazmış olabilir. Ham gövde aşağıda görünüyor.",
                  },
                  payload_not_dict_or_list: {
                    title: "⏭️ İŞLENMEDİ — Geçersiz JSON Şekli",
                    explain: "JSON parse edildi ama dict ya da list değil (scalar geldi). ITSAPI protokolüne uymuyor.",
                  },
                };
                const meta = SKIP_META[detail.skip_reason] || {
                  title: `⏭️ İŞLENMEDİ — ${detail.skip_reason}`,
                  explain: "Bilinmeyen skip nedeni — kodda yeni bir sebep etiketi eklenmiş olabilir. Backend loglarına bakın.",
                };
                return (
                  <div
                    className="rounded-sm border border-neutral-300 bg-neutral-50 p-3"
                    data-testid="cameraraw-detail-skipped"
                  >
                    <div className="text-[10px] uppercase tracking-widest font-semibold mb-2 text-neutral-700">
                      {meta.title}
                    </div>
                    <div className="text-xs font-mono text-neutral-700 mb-2">
                      skip_reason = <strong>{detail.skip_reason}</strong>
                    </div>
                    <div className="text-[11px] text-muted-foreground italic border-t border-border/50 pt-2">
                      {meta.explain}
                    </div>
                  </div>
                );
              })()}
              {/* İşleme Sonucu — üstte, göze çarpsın */}
              {(detail.processed || detail.process_error) && (() => {
                const p = detail.processed || {};
                const REASON_META = {
                  duplicate: { title: "⏱ MÜKERRER OKUMA (60sn Penceresi)", cls: "border-slate-300 bg-slate-50", explain: "Aynı plaka+kamera son 60 saniye içinde işlendi — Dahua ITC kameraları normal bir olay için 2-4 arka arkaya frame gönderir; ilk okuma oturumu açtı, bu ve sonraki mükerrer okumalar bilinçli olarak yok sayıldı. Hata DEĞİL, tasarım gereği bir kabul." },
                  already_inside: { title: "🚗 ARAÇ ZATEN İÇERİDE", cls: "border-cyan-300 bg-cyan-50", explain: "Kamera direction_hint = entry_only ve bu plakanın açık bir oturumu var — mükerrer giriş engellendi." },
                  no_open_session: { title: "❔ ÇIKIŞTA GİRİŞ KAYDI YOK", cls: "border-orange-300 bg-orange-50", explain: "Bu çıkış okuması için açık giriş bulunamadı — Eşleşmeyen Çıkışlar'a düştü." },
                  blacklisted: { title: "🚫 KARA LİSTEDE", cls: "border-red-300 bg-red-50", explain: "Plaka kara listede — okuma reddedildi, operatöre push bildirimi gönderildi." },
                  lot_full: { title: "🅿️ OTOPARK DOLU", cls: "border-red-400 bg-red-100", explain: "Otopark %100 dolulukta ve settings.block_when_full = true; giriş reddedildi." },
                  plate_flicker: { title: "🔁 OCR TİTREME — REDDEDİLDİ", cls: "border-amber-300 bg-amber-50", explain: "Aynı otoparkta son N saniye içinde plakası 1 karakter farklı bir açık kayıt var. OCR titremesi olabilir." },
                  phantom_entry_prevented: { title: "🛡️ FANTOM GİRİŞ ENGELLENDİ", cls: "border-blue-300 bg-blue-50", explain: "Bu okuma bir OCR yanılmasıyla yeni giriş olarak yorumlanmak üzereydi. Aynı otoparkta yakın plaka(lar) açık." },
                  duplicate_after_exit: { title: "⏱ POST-EXIT COOLDOWN", cls: "border-purple-300 bg-purple-50", explain: "Bu plaka yakın zamanda çıkmış, aynı okumayı tekrar giriş olarak kabul etmedik." },
                  dedupe_locked: { title: "🔒 DEDUPE (Sliding Window)", cls: "border-gray-300 bg-gray-50", explain: "Aynı plaka+kamera son cooldown penceresi içinde tetiklendi." },
                  low_confidence: { title: "📉 DÜŞÜK GÜVEN", cls: "border-yellow-300 bg-yellow-50", explain: "OCR güven seviyesi eşiğin altında." },
                  unrecognized_format_needs_review: { title: "🌐 TANINMAYAN PLAKA FORMATI", cls: "border-rose-300 bg-rose-50", explain: "Plaka hiçbir bilinen ülke formatına uymuyor VE güven düşük/verilmemiş — muhtemelen t-shirt yazısı, duvar deseni veya kamera OCR yanılması. Operatör onayı bekleniyor." },
                };
                const rejected = p.ok === false;
                const reasonMeta = rejected && p.reason ? REASON_META[p.reason] : null;
                return (
                  <div className={`rounded-sm border p-3 ${
                    p.ok ? "border-green-300 bg-green-50" :
                    reasonMeta ? reasonMeta.cls :
                    rejected || detail.process_error ? "border-red-300 bg-red-50" :
                    "border-muted"
                  }`} data-testid="cameraraw-detail-processed">
                    <div className="text-[10px] uppercase tracking-widest font-semibold mb-2">
                      {p.ok ? "✓ Araç Kaydına Dönüştürüldü" : reasonMeta ? reasonMeta.title : "✗ İşleme Başarısız"}
                    </div>
                    {p.ok ? (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                        <div><span className="text-muted-foreground">Plaka:</span> <strong>{p.plate}</strong></div>
                        <div><span className="text-muted-foreground">Yön:</span> <strong>{p.direction === "giris" ? "GİRİŞ" : p.direction === "cikis" ? "ÇIKIŞ" : p.direction || "-"}</strong></div>
                        <div><span className="text-muted-foreground">Cihaz ID:</span> {p.device_id || "—"}</div>
                        <div><span className="text-muted-foreground">Kamera:</span> {p.camera_id || "—"}</div>
                        {p.detected_country && (
                          <div><span className="text-muted-foreground">Ülke:</span> {p.detected_country}</div>
                        )}
                        {p.capture_time && (
                          <div><span className="text-muted-foreground">Kamera Zamanı:</span> {formatTimestamp(p.capture_time)}</div>
                        )}
                        {p.session_id && (
                          <div className="col-span-2"><span className="text-muted-foreground">Oturum ID:</span> {p.session_id}</div>
                        )}
                        {p.saved_image_url && (
                          <div className="col-span-2 mt-2">
                            <div className="text-muted-foreground mb-1">Kaydedilen Fotoğraf:</div>
                            <a href={p.saved_image_url} target="_blank" rel="noreferrer">
                              <img
                                src={p.saved_image_url}
                                alt="ANPR"
                                className="max-h-32 rounded border border-border"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </a>
                          </div>
                        )}
                      </div>
                    ) : reasonMeta ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                          <div><span className="text-muted-foreground">Plaka:</span> <strong>{p.plate || "—"}</strong></div>
                          <div><span className="text-muted-foreground">Kamera:</span> {p.camera_id || "—"}</div>
                          {p.matched_plate && (
                            <div><span className="text-muted-foreground">Benzer Plaka:</span> <strong>{p.matched_plate}</strong></div>
                          )}
                          {p.distance != null && (
                            <div><span className="text-muted-foreground">Karakter Farkı:</span> {p.distance}</div>
                          )}
                          {p.match_kind && (
                            <div><span className="text-muted-foreground">Eşleşme Tipi:</span> {p.match_kind}</div>
                          )}
                          {p.unmatched_exit_id && (
                            <div className="col-span-2"><span className="text-muted-foreground">Unmatched Exit ID:</span> {p.unmatched_exit_id}</div>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground italic border-t border-border/50 pt-2">
                          {reasonMeta.explain}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs font-mono text-red-800 break-all">
                        {p.error || detail.process_error
                          || (p.reason ? `Reddedildi (reason=${p.reason})` : null)
                          || "Bilinmeyen hata"}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Zaman</div>
                  <div className="font-mono">{formatTimestamp(detail.timestamp)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">İstemci IP</div>
                  <div className="font-mono">{detail.client_ip || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Content-Type</div>
                  <div className="font-mono text-xs">{detail.content_type || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Boyut</div>
                  <div className="font-mono">
                    {bytesPretty(detail.content_length)} · <span className="text-muted-foreground">{detail.body_encoding}</span>
                    {detail.body_truncated && <Badge variant="destructive" className="ml-2 text-[9px]">KESİLDİ</Badge>}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tam URL</div>
                  <button onClick={() => copyToClipboard(detail.url)} className="text-primary text-xs flex items-center gap-1">
                    <Copy size={11} /> Kopyala
                  </button>
                </div>
                <div className="font-mono text-xs bg-muted p-2 rounded break-all">{detail.url}</div>
              </div>

              {Object.keys(detail.query_params || {}).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Query Params</div>
                  <div className="bg-muted rounded p-2 text-xs font-mono">
                    {Object.entries(detail.query_params).map(([k, v]) => (
                      <div key={k}><span className="text-primary">{k}</span>: {String(v)}</div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">HTTP Headers</div>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(detail.headers, null, 2), "Headers kopyalandı")}
                    className="text-primary text-xs flex items-center gap-1"
                  >
                    <Copy size={11} /> Kopyala
                  </button>
                </div>
                <div className="bg-muted rounded p-2 text-xs font-mono max-h-40 overflow-y-auto">
                  {Object.entries(detail.headers || {}).map(([k, v]) => (
                    <div key={k}><span className="text-primary">{k}</span>: {String(v)}</div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Body ({detail.body_encoding})
                  </div>
                  <button
                    onClick={() => copyToClipboard(detail.body_text, "Body kopyalandı")}
                    className="text-primary text-xs flex items-center gap-1"
                  >
                    <Copy size={11} /> Kopyala
                  </button>
                </div>
                <pre
                  className="bg-muted rounded p-3 text-xs font-mono max-h-80 overflow-auto whitespace-pre-wrap break-all"
                  data-testid="cameraraw-detail-body"
                >
                  {detail.body_encoding === "utf-8" ? tryFormatJson(detail.body_text) : detail.body_text || "(boş)"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
